// src/components/Navbar.js
import React, { useEffect, useRef, useState, useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import "./navbar.css";

export default function Navbar() {
  const location = useLocation();
  const pathnameRaw = location.pathname || "/";
  const isHome = pathnameRaw === "/";

  /* -------------------------------------------------
     ROUTE RULES FOR NAV INK + BODY ATTRIBUTE
     ------------------------------------------------- */

  // Shop slugs that live on pale paper backgrounds → navbar should be BLACK ink
  const LIGHT_SHOP_SLUGS = [
    "portraits",
    "wildlife",
    "white-continent",
    "unspoken-journeys",
    "paintings",
    "paintings/:id",
  ];

  // Shop slugs that live on dark / warm / cinematic backgrounds → navbar should be WHITE ink
  const DARK_SHOP_SLUGS = ["silent-vessels", "fading"];

  // Other light pages (non-shop) we already know want black ink
  const LIGHT_PREFIXES = ["/commercial", "/photographs", "/paintings"];

  // Other dark pages we know must stay white ink even if they match other rules
  // (right now: the two special dark shop series)
  const DARK_PREFIXES = ["/shop/silent-vessels", "/shop/fading"];

  // helper: quick route classifier by prefix
  function pathStartsWithAny(path, arr) {
    return arr.some((prefix) => path.startsWith(prefix));
  }

  /* -------------------------------------------------
     1. CONTROL <body data-light-nav="true"> DYNAMICALLY
        based on slug at the END of /shop/:slug
     ------------------------------------------------- */
  useEffect(() => {
    const path = (location.pathname || "").replace(/\/+$/, "");
    const slug = path.split("/").filter(Boolean).slice(-1)[0] || "";

    // DARK shop pages → remove attribute (so nav stays white ink)
    if (DARK_SHOP_SLUGS.includes(slug)) {
      document.body.removeAttribute("data-light-nav");
    }
    // LIGHT shop pages → force attribute (so nav goes black ink)
    else if (LIGHT_SHOP_SLUGS.includes(slug)) {
      document.body.setAttribute("data-light-nav", "true");
    }
    // fallback:
    else {
      document.body.removeAttribute("data-light-nav");
    }

    // cleanup so it doesn't "leak" if you navigate away
    return () => {
      document.body.removeAttribute("data-light-nav");
    };
  }, [location.pathname]); // runs every route change

  /* -------------------------------------------------
     2. desiredDarkInk:
        true  = use black ink (navbar gets .ink-dark)
        false = use white ink
     ------------------------------------------------- */

  const desiredDarkInk = useMemo(() => {
    const p = pathnameRaw.replace(/\/+$/, "");
    const slug = p.split("/").filter(Boolean).slice(-1)[0] || "";

    // Explicit dark/cinematic shop series → keep WHITE ink
    if (DARK_SHOP_SLUGS.includes(slug)) {
      return false; // white text
    }

    // Light / paper shop series → use BLACK ink
    if (LIGHT_SHOP_SLUGS.includes(slug)) {
      return true; // black text
    }

    // Non-shop pages that should also get black ink
    if (pathStartsWithAny(p, LIGHT_PREFIXES)) {
      return true; // black text
    }

    // Everything else defaults to white text
    return false;
  }, [pathnameRaw]);

  /* -------------------------------------------------
     STATE
     ------------------------------------------------- */
  const [mobileNav, setMobileNav] = useState(false);
  const [expandedMain, setExpandedMain] = useState(null); // 'continents' | 'shop'
  const [expandedContinent, setExpandedContinent] = useState(null); // e.g. 'Africa'
  const [expandedL2, setExpandedL2] = useState(null); // e.g. 'Asia:Myanmar'
  const [mounted, setMounted] = useState(false);
  const [hidden, setHidden] = useState(false);
  const [atTop, setAtTop] = useState(true);

  // this is what actually controls the .ink-dark class
  const [darkInk, setDarkInk] = useState(desiredDarkInk);

  // whenever desiredDarkInk changes, sync it into state
  useEffect(() => {
    setDarkInk(desiredDarkInk);
  }, [desiredDarkInk]);

  const navRef = useRef(null);

  // Visible navbar video (in the mobile overlay)
  const videoRef = useRef(null);

  // Hidden preloader kept mounted at all times
  const PRELOAD_SRC =
    "https://lvltravel.s3.us-east-2.amazonaws.com/AmazonasNVid.mp4";
  const preloadRef = useRef(null);

  /* -------------------------------------------------
     DATA (continents + shop)
     ------------------------------------------------- */
  const CONTINENTS = [
    "Africa",
    "Asia",
    "South America",
    "North America",
    "Europe",
    "Antarctica",
    "Australia",
  ];

  // Any item may be: { name, path } OR { name, children: [{ name, path }, ...] }
  const subLinks = {
    Africa: [
      { name: "Madagascar", path: "/africa/madagascar" },
      { name: "Madagascar – Wildlife", path: "/africa/madagascar-wildlife" },
      { name: "South Africa – Wildlife", path: "/africa/south-africa-wildlife" },
      { name: "South Africa - Capetown", path: "/africa/capetown" },
      { name: "Lesotho", path: "/africa/lesotho" },
      { name: "Morocco - COMING SOON", path: "/" },
      { name: "Eswatini - COMING SOON", path: "/" },
    ],
    Asia: [
      { name: "India", path: "/asia/india" },
      { name: "Bhutan", path: "/asia/bhutan" },
      { name: "Nepal", path: "/asia/nepal" },
      {
        name: "Myanmar",
        children: [
          { name: "The People", path: "/asia/myanmar-people" },
          { name: "Fields of Joy", path: "/asia/myanmar-fields" },
        ],
      },
      { name: "Laos", path: "/asia/laos" },
      { name: "Vietnam", path: "/asia/vietnam" },
      { name: "Cambodia", path: "/asia/cambodia" },
      { name: "Japan - COMING SOON", path: "/" },
      { name: "Thailand - COMING SOON", path: "/" },
      { name: "Indonesia - COMING SOON", path: "/" },
      { name: "Singapore - COMING SOON", path: "/" },
    ],
    SouthAmerica: [
      {
        name: "Brazil",
        children: [
          { name: "Amazonas", path: "/southamerica/brazil-amazon" },
          { name: "Buzios", path: "/southamerica/brazil-buzios" },
        ],
      },
      { name: "Argentina - Ushuaia", path: "/southamerica/argentina-ushuaia" },
    ],
    NorthAmerica: [{ name: "Mexico - COMING SOON", path: "/northamerica/mexico" }],
    Europe: [{ name: "COMING SOON", path: "/" }],
    Australia: [
      { name: "Sydney - Bondi Beach", path: "/australia/sydney-bondibeach" },
      { name: "Sydney", path: "/australia/sydney" },
    ],

    Shop: [
      { name: "Portraits", path: "/shop/portraits" },
      { name: "Landscapes & Wildlife", path: "/shop/wildlife" },
      { name: "The White Continent", path: "/shop/white-continent" },
      { name: "Unspoken Journeys", path: "/shop/unspoken-journeys" },
      { name: "Silent Vessels", path: "/shop/silent-vessels" },
      { name: "The Fading Light", path: "/shop/fading" },
      { name: "Paintings", path: "/shop/paintings" },
    ],
  };

  /* -------------------------------------------------
     PRELOAD / WARM VIDEO
     ------------------------------------------------- */

  // Preload hidden <video> element
  useEffect(() => {
    const el = preloadRef.current;
    if (!el) return;
    const id = window.requestIdleCallback
      ? window.requestIdleCallback(() => {
          try {
            el.load();
          } catch {}
        })
      : setTimeout(() => {
          try {
            el.load();
          } catch {}
        }, 150);
    return () => {
      window.cancelIdleCallback?.(id);
      clearTimeout(id);
    };
  }, []);

  // Warm the video URL into cache
  useEffect(() => {
    const warm = async () => {
      try {
        const res = await fetch(PRELOAD_SRC, {
          mode: "cors",
          cache: "force-cache",
        });
        await res.body?.getReader()?.read();
      } catch {}
    };
    const id = setTimeout(warm, 500);
    return () => clearTimeout(id);
  }, []);

  // autoplay the bg video when mobile nav is opened
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    if (mobileNav) {
      if (!v.src || v.src !== PRELOAD_SRC) v.src = PRELOAD_SRC;
      const tryPlay = () => v.play().catch(() => {});
      if (v.readyState >= 2) tryPlay();
      else v.addEventListener("loadeddata", tryPlay, { once: true });
    } else {
      try {
        v.pause();
      } catch {}
    }
  }, [mobileNav]);

  /* -------------------------------------------------
     BEHAVIOR EFFECTS
     ------------------------------------------------- */

  // mark component mounted for CSS transitions
  useEffect(() => {
    const t = requestAnimationFrame(() => setMounted(true));
    return () => cancelAnimationFrame(t);
  }, []);

  // hide-on-scroll behavior
  useEffect(() => {
    let lastY = window.scrollY;
    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        const y = window.scrollY;
        const dy = y - lastY;
        setAtTop(y < 8);
        if (!mobileNav && Math.abs(dy) > 6) {
          setHidden(dy > 0 && y > 80);
        } else if (mobileNav) {
          setHidden(false);
        }
        lastY = y;
        ticking = false;
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [mobileNav]);

  // keep bar visible while mobile menu is open
  useEffect(() => {
    if (mobileNav) setHidden(false);
  }, [mobileNav]);

  // reset menus on route change
  useEffect(() => {
    setMobileNav(false);
    setExpandedMain(null);
    setExpandedContinent(null);
    setExpandedL2(null);
  }, [pathnameRaw]);

  // ESC closes menus
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") {
        setExpandedMain(null);
        setExpandedContinent(null);
        setExpandedL2(null);
        setMobileNav(false);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // click outside of nav closes desktop dropdowns
  useEffect(() => {
    const onPointerDown = (e) => {
      if (!navRef.current) return;
      if (!navRef.current.contains(e.target)) {
        setExpandedMain(null);
        setExpandedContinent(null);
        setExpandedL2(null);
      }
    };
    document.addEventListener("pointerdown", onPointerDown, { passive: true });
    document.addEventListener("mousedown", onPointerDown, { passive: true });
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("mousedown", onPointerDown);
    };
  }, []);

  // lock page scroll behind mobile overlay
  useEffect(() => {
    if (!mobileNav) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [mobileNav]);

  /* -------------------------------------------------
     HELPERS
     ------------------------------------------------- */

  const toggleMobileNav = () => setMobileNav((v) => !v);

  const closeMobileNav = () => {
    setMobileNav(false);
    setExpandedMain(null);
    setExpandedContinent(null);
    setExpandedL2(null);
    setHidden(false);
    setAtTop(window.scrollY < 8);

    const v = videoRef.current;
    if (v) {
      try {
        v.pause();
        v.currentTime = 0;
      } catch {}
    }
  };

  const toggleMain = (key) => {
    setExpandedMain((curr) => (curr === key ? null : key));
    setExpandedContinent(null);
    setExpandedL2(null);
  };

  const toggleContinent = (name) => {
    setExpandedContinent((curr) => (curr === name ? null : name));
    setExpandedL2(null);
  };

  const toggleNestedL2 = (continent, name) => {
    const k = `${continent}:${name}`;
    setExpandedL2((curr) => (curr === k ? null : k));
  };

  const slug = (str) => str.toLowerCase().replace(/\s+/g, "-");

  const mapContinentKey = (name) =>
    name === "South America"
      ? "SouthAmerica"
      : name === "North America"
      ? "NorthAmerica"
      : name;

  const getSubForContinent = (name) => subLinks[mapContinentKey(name)] || [];
  const hasSubForContinent = (name) => getSubForContinent(name).length > 0;

  const hasDropdownOpen = !!expandedMain || !!expandedContinent;

  // Antarctica special-case path
  const linkForContinent = (name) =>
    name === "Antarctica" ? "/antarctica/antarctica" : `/${slug(name)}`;

  /* -------------------------------------------------
     RENDER
     ------------------------------------------------- */

  return (
    <header
      ref={navRef}
      className={`topbar ${mounted ? "mounted" : ""} ${
        hidden ? "nav-hidden" : "nav-visible"
      } ${atTop ? "at-top" : "scrolled"} ${
        hasDropdownOpen ? "has-dropdown" : ""
      } ${mobileNav ? "mobile-open" : ""} ${darkInk ? "ink-dark" : ""}`}
      data-page={isHome ? "home" : "inner"}
      role="banner"
    >
      <div className="topbar-inner">
        <Link
          className="brand"
          to="/"
          onClick={() => {
            setExpandedMain(null);
            setExpandedContinent(null);
            closeMobileNav();
          }}
        >
          LVL
        </Link>

        {/* Desktop nav */}
        <nav className="desktop-nav" aria-label="primary">
          {/* Shop dropdown */}
          <div className={`menu has-sub ${expandedMain === "shop" ? "open" : ""}`}>
            <button
              className="nav-link btn"
              onClick={() => toggleMain("shop")}
              aria-expanded={expandedMain === "shop"}
              aria-controls="menu-shop"
            >
              Fine Art Editions
            </button>

            <div
              id="menu-shop"
              className={`dropdown ${expandedMain === "shop" ? "open" : ""}`}
              role="menu"
            >
              {(subLinks.Shop || []).map((s) => {
                const isPaintings = s.path === "/shop/paintings";

                if (isPaintings) {
                  return (
                    <button
                      key={s.name}
                      type="button"
                      className="drop-item coming-soon-wrap"
                      onClick={() => {
                        setExpandedMain(null);
                      }}
                    >
                      <span className="coming-title">{s.name}</span>
                      <span className="coming-sub"> &nbsp;Coming soon</span>
                    </button>
                  );
                }

                return (
                  <Link
                    key={s.name}
                    className="drop-item"
                    to={s.path}
                    onClick={() => setExpandedMain(null)}
                  >
                    {s.name}
                  </Link>
                );
              })}
            </div>
          </div>

          {/* Continents top-level */}
          <div
            className={`menu has-sub ${
              expandedMain === "continents" ? "open" : ""
            }`}
          >
            <button
              className="nav-link btn"
              onClick={() => toggleMain("continents")}
              aria-expanded={expandedMain === "continents"}
              aria-controls="menu-continents"
            >
              Across 7 Continents
            </button>
            <div
              id="menu-continents"
              className={`dropdown ${expandedMain === "continents" ? "open" : ""}`}
              role="menu"
            >
              {CONTINENTS.map((c) => {
                const cHas = hasSubForContinent(c);
                if (!cHas) {
                  // just link (Antarctica is special cased)
                  return (
                    <Link
                      key={c}
                      className="drop-item"
                      to={linkForContinent(c)}
                      onClick={() => setExpandedMain(null)}
                    >
                      {c}
                    </Link>
                  );
                }

                const cOpen = expandedContinent === c;
                const sub = getSubForContinent(c);

                return (
                  <div key={c} className={`submenu ${cOpen ? "open" : ""}`}>
                    <button
                      type="button"
                      className="drop-item btn"
                      onClick={() => toggleContinent(c)}
                      aria-expanded={cOpen}
                    >
                      {c}
                    </button>

                    <div className={`submenu-panel ${cOpen ? "show" : ""}`}>
                      {sub.map((s) => {
                        // third level?
                        if (s.children && s.children.length) {
                          const comboKey = `${mapContinentKey(c)}:${s.name}`;
                          const open = expandedL2 === comboKey;

                          return (
                            <div
                              key={s.name}
                              className={`submenu lvl2 ${open ? "open" : ""}`}
                            >
                              <button
                                type="button"
                                className="drop-item btn lvl2"
                                onClick={() =>
                                  toggleNestedL2(mapContinentKey(c), s.name)
                                }
                                aria-expanded={open}
                              >
                                {s.name}
                              </button>

                              <div
                                className={`submenu-panel ${open ? "show" : ""}`}
                              >
                                {s.children.map((child) => (
                                  <Link
                                    key={child.name}
                                    className="drop-item lvl3"
                                    to={child.path}
                                    onClick={() => {
                                      setExpandedMain(null);
                                      setExpandedContinent(null);
                                      setExpandedL2(null);
                                    }}
                                  >
                                    {child.name}
                                  </Link>
                                ))}
                              </div>
                            </div>
                          );
                        }

                        // normal second-level link
                        return (
                          <Link
                            key={s.name}
                            className="drop-item lvl2"
                            to={s.path}
                            onClick={() => {
                              setExpandedMain(null);
                              setExpandedContinent(null);
                            }}
                          >
                            {s.name}
                          </Link>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Other desktop links */}
          <Link className="nav-link" to="/humanitarianism">
            Humanitarianism
          </Link>
          <Link className="nav-link" to="/fashion">
            Fashion
          </Link>
          <Link className="nav-link" to="/commercial">
            Commercial Portfolio
          </Link>
          <Link className="nav-link" to="/world-rated">
            World Rated Hotels
          </Link>
        </nav>

        {/* Mobile trigger */}
        <button
          type="button"
          onClick={toggleMobileNav}
          className={`mobile-trigger ${mobileNav ? "active" : ""}`}
          aria-expanded={mobileNav}
          aria-controls="mobile-navbar"
          aria-label="Open menu"
        >
          Discover Horizons
        </button>
      </div>

      {/* Mobile overlay */}
      {mobileNav && (
        <div
          id="mobile-navbar"
          className="mobile-panel"
          role="dialog"
          aria-modal="true"
        >
          {/* Background video layer */}
          <video
            ref={videoRef}
            className="mobile-bg"
            src={PRELOAD_SRC}
            autoPlay
            loop
            muted
            playsInline
            preload="auto"
            poster="https://lvltravel.s3.us-east-2.amazonaws.com/0001.jpg"
          />

          {/* Foreground content layer */}
          <div className="mobile-overlay">
            <div className="mobile-head">
              <span className="overlay-title">Explore</span>
              <button
                className="overlay-close"
                onClick={closeMobileNav}
                aria-label="Close menu"
              >
                Close
              </button>
            </div>

            <p className="mobile-intro">
              Journey through the 7 Continents. Endless Beauty. Countless Secrets.
              <br />
              <br />
              <span className="italic">
                Trace the pulse of jungles, dunes, ice, and ancient stone — and
                lose yourself in the silence between.
              </span>
            </p>

            <div className="mobile-accordion">
              {/* Shop */}
              <button
                type="button"
                className="m-link lvl0"
                onClick={() => toggleMain("shop")}
                aria-expanded={expandedMain === "shop"}
              >
                Fine Art Editions
              </button>

              {expandedMain === "shop" && (
                <div className="m-sub depth1">
                  {(subLinks.Shop || []).map((s) => {
                    const isPaintings = s.path === "/shop/paintings";

                    if (isPaintings) {
                      return (
                        <button
                          key={s.name}
                          type="button"
                          className="m-link lvl1 coming-soon-wrap"
                          onClick={() => {
                            closeMobileNav();
                          }}
                        >
                          <span className="coming-title">{s.name}</span>
                          <span className="coming-sub"> &nbsp;Coming soon</span>
                        </button>
                      );
                    }

                    return (
                      <Link
                        key={s.name}
                        className="m-link lvl1"
                        to={s.path}
                        onClick={closeMobileNav}
                      >
                        {s.name}
                      </Link>
                    );
                  })}
                </div>
              )}

              {/* Continents */}
              <button
                type="button"
                className="m-link lvl0"
                onClick={() => toggleMain("continents")}
                aria-expanded={expandedMain === "continents"}
                aria-controls="mobile-continents"
              >
                Across 7 Continents
              </button>

              {expandedMain === "continents" && (
                <div id="mobile-continents" className="m-sub depth1">
                  {CONTINENTS.map((c) => {
                    const cOpen = expandedContinent === c;
                    const items = getSubForContinent(c);
                    const has = items.length > 0;

                    if (!has) {
                      // no submenu, just link (Antarctica has custom path)
                      return (
                        <Link
                          key={c}
                          className="m-link lvl1"
                          to={linkForContinent(c)}
                          onClick={() => {
                            setExpandedMain(null);
                            setExpandedContinent(null);
                            closeMobileNav();
                          }}
                        >
                          {c}
                        </Link>
                      );
                    }

                    return (
                      <div key={c} className="m-accordion">
                        <button
                          type="button"
                          className="m-link lvl1"
                          onClick={() => toggleContinent(c)}
                          aria-expanded={cOpen}
                        >
                          {c}
                        </button>

                        {cOpen && (
                          <div className="m-sub depth2">
                            {items.map((s) => {
                              // third level?
                              if (s.children && s.children.length) {
                                const comboKey = `${mapContinentKey(c)}:${s.name}`;
                                const open = expandedL2 === comboKey;

                                return (
                                  <div key={s.name} className="m-accordion">
                                    <button
                                      type="button"
                                      className="m-link lvl2"
                                      onClick={() =>
                                        toggleNestedL2(mapContinentKey(c), s.name)
                                      }
                                      aria-expanded={open}
                                    >
                                      {s.name}
                                    </button>

                                    {open && (
                                      <div className="m-sub depth3">
                                        {s.children.map((child) => (
                                          <Link
                                            key={child.name}
                                            className="m-link lvl3"
                                            to={child.path}
                                            onClick={() => {
                                              setExpandedMain(null);
                                              setExpandedContinent(null);
                                              setExpandedL2(null);
                                              closeMobileNav();
                                            }}
                                          >
                                            {child.name}
                                          </Link>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                );
                              }

                              // regular child link
                              return (
                                <Link
                                  key={s.name}
                                  className="m-link lvl2"
                                  to={s.path}
                                  onClick={() => {
                                    setExpandedMain(null);
                                    setExpandedContinent(null);
                                    closeMobileNav();
                                  }}
                                >
                                  {s.name}
                                </Link>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Other sections */}
              <Link
                className="m-link lvl0"
                to="/humanitarianism"
                onClick={closeMobileNav}
              >
                Humanitarianism
              </Link>

              <Link
                to="/fashion"
                className="m-link lvl0"
                onClick={closeMobileNav}
              >
                Fashion
              </Link>

              <Link
                to="/commercial"
                className="m-link lvl0"
                onClick={closeMobileNav}
              >
                Commercial Portfolio
              </Link>

              <Link
                to="/world-rated"
                className="m-link lvl0"
                onClick={closeMobileNav}
              >
                World Rated Hotels
              </Link>
            </div>
          </div>
        </div>
      )}

      {/* Hidden preloader (always mounted) */}
      <video
        ref={preloadRef}
        src={PRELOAD_SRC}
        preload="auto"
        muted
        playsInline
        style={{
          position: "absolute",
          width: 1,
          height: 1,
          opacity: 0,
          pointerEvents: "none",
        }}
      />
    </header>
  );
}
